import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const textClass = scrolled
    ? "text-foreground-900"
    : "text-background-50";
  const textSubClass = scrolled
    ? "text-foreground-700 hover:text-foreground-950"
    : "text-background-50/80 hover:text-background-50";
  const buyBtnClass = scrolled
    ? "border-foreground-900 text-foreground-900 hover:bg-foreground-900 hover:text-background-50"
    : "border-background-50 text-background-50 hover:bg-background-50 hover:text-foreground-900";
  const primaryBtnClass = scrolled
    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
    : "bg-background-50 text-foreground-900 hover:bg-background-100";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-10 py-3 md:py-4">
        <Link to="/" className="flex items-center gap-2 group cursor-pointer flex-shrink-0">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${scrolled ? "bg-primary-500" : "bg-background-50/25 backdrop-blur-sm"}`}>
            <i className={`ri-file-list-3-line text-lg ${scrolled ? "text-background-50" : "text-background-50"}`}></i>
          </div>
          <span className={`font-heading text-lg font-semibold hidden sm:block whitespace-nowrap ${textClass}`}>
            CSSBuy Spreadsheet
          </span>
        </Link>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-1">
          <Link to="/" className={`px-4 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer ${textSubClass}`}>
            Home
          </Link>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "bg-foreground-900 text-background-50 hover:bg-foreground-800"
                  : "bg-background-50/20 backdrop-blur-sm text-background-50 hover:bg-background-50/30 border border-background-50/20"
              }`}
            >
              Categorias
              <i className={`ri-arrow-down-s-line transition-transform duration-200 text-base ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-[420px] overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <span className="w-7 h-7 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-right-up-line text-accent-600 text-xs"></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/artigo/como-usar-cssbuy-spreadsheet-passo-a-passo"
            className={`px-4 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer ${textSubClass}`}
          >
            Guia de Compras
          </Link>
          <Link to="/blog" className={`px-4 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer ${textSubClass}`}>
            Blog
          </Link>
        </div>

        {/* Desktop CTA buttons */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${primaryBtnClass}`}
          >
            cssbuy spreadsheet
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${buyBtnClass}`}
          >
            Buy It Now
            <i className="ri-shopping-cart-line text-sm"></i>
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden w-10 h-10 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
            scrolled
              ? "text-foreground-900 hover:bg-background-100"
              : "text-background-50 hover:bg-background-50/15"
          }`}
          aria-label="Menu"
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-[57px] bg-background-50 z-40 overflow-y-auto">
          <div className="px-4 py-6 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-900 font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-home-4-line text-accent-600"></i>
              </span>
              Home
            </Link>

            <Link
              to="/artigo/como-usar-cssbuy-spreadsheet-passo-a-passo"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-900 font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
                <i className="ri-book-open-line text-secondary-600"></i>
              </span>
              Guia de Compras
            </Link>

            <Link
              to="/blog"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-900 font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-article-line text-accent-600"></i>
              </span>
              Blog
            </Link>

            {/* Mobile categories */}
            <div>
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-foreground-900 font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-grid-line text-primary-600"></i>
                  </span>
                  Categorias
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-lg ${mobileCatOpen ? "rotate-180" : ""}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="ml-4 mt-1 border-l-2 border-background-200 pl-4 space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => { setMobileOpen(false); setMobileCatOpen(false); }}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    >
                      <span className="w-6 h-6 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-right-up-line text-accent-600 text-xs"></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200 my-3"></div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              cssbuy spreadsheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full border-2 border-foreground-900 text-foreground-900 text-sm font-semibold hover:bg-foreground-900 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap mt-2"
            >
              Buy It Now
              <i className="ri-shopping-cart-line text-sm"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}